import { string } from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';
import {
  params,
  string as stringType,
  shape as shapeType,
  arrayOf as arrayType,
  useDataModel
} from '@thd-nucleus/data-sources';

const rentalDescription = (productLabel) => `Rent a ${productLabel} from one of our over 1,200 THD rental locations.`
+ ' Call your local store for same day pickup availability.';

const productStructure = (data, itemId) => {
  const { identifiers, media } = data?.product || {};
  const { brandName, canonicalUrl, productLabel } = identifiers;
  const { url } = media?.image || '';
  const description = rentalDescription(productLabel);
  const primaryImage = url;
  const canonical = `https://www.homedepot.com${canonicalUrl}`;

  JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: productLabel,
    image: primaryImage,
    description,
    productID: itemId,
    sku: '',
    gtin13: '',
    depth: '',
    height: '',
    width: '',
    color: '',
    weight: '',
    brand: {
      '@type': 'Thing',
      name: brandName,
    },
    offers: {
      '@type': 'Offer',
      url: canonical,
      priceCurrency: 'USD',
      price: '',
      priceValidUntil: '',
      availability: 'https://schema.org/InStock',
    }
  })
};

const constructTitle = ({ brandName, productLabel, modelNumber }) => {
  const parts = [
    brandName?.trim(),
    productLabel?.trim(),
    modelNumber?.trim(),
    '- The Home Depot',
  ].filter((part) => part);

  return parts.join(' ');
};

const rentalsHandler = ({ data, _props }) => {
  const tags = [];

  const { canonicalUrl, brandName, productLabel, modelNumber } = data?.product?.identifiers || {};
  const canonical = `https://www.homedepot.com${canonicalUrl}`;

  const title = constructTitle({ brandName, productLabel, modelNumber });
  const description = rentalDescription(productLabel);

  tags.push(new TitleTag(title));
  tags.push(new MetaTag({ name: 'description', content: description, id: 'prop-description' }));

  tags.push(new LinkTag({
    rel: 'canonical', href: canonical, id: 'canonical'
  }));

  if (data?.pageType === 'reservations') {
    tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX, NOFOLLOW', id: 'robots' }));
  }

  tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));
  tags.push(new MetaTag({ name: 'og:title', content: title, id: 'og-title' }));
  tags.push(new MetaTag({ name: 'og:description', content: description, id: 'og-description' }));
  tags.push(new MetaTag({ name: 'og:url', content: canonical, id: 'og-url' }));
  tags.push(new MetaTag({ name: 'og:type', content: 'rentals', id: 'og-type' }));
  tags.push(new MetaTag({ name: 'og:site_name', content: 'The Home Depot', id: 'og-site_name' }));
  tags.push(new MetaTag({ name: 'fb:admins', content: '100005416960152', id: 'fb-admins' }));

  return tags;
};

export const ProductMetadata = ({ itemId, pageType }) => {
  const { data, loading, error } = useDataModel('product', { variables: { itemId } });

  const deps = data?.product.identifiers;

  const helmetData = {
    product: data?.product,
    pageType
  };

  useHelmet('rentalsMetadata', { data: helmetData, props: { itemId } }, rentalsHandler, [deps]);
  return null;
}

ProductMetadata.propTypes = {
  itemId: string.isRequired,
  pageType: string
}

ProductMetadata.defaultProps = {
  pageType: ''
};

ProductMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
    dataSources: stringType(),
    itemId: stringType(),
    identifiers: shapeType({
      canonicalUrl: stringType(),
      brandName: stringType(),
      productLabel: stringType(),
      modelNumber: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        sizes: arrayType(stringType()),
        subType: stringType(),
        type: stringType(),
        url: stringType()
      }))
    }),
  })
}